
import { useState } from 'react';
import { useReports } from './hooks/useReports';
import { exportPDF } from './lib/pdfExport';

export default function AuditReports() {
  const { audit } = useReports();
  const [period, setPeriod] = useState({
    startDate: '',
    endDate: '',
    module: '',
    user: ''
  });
  
  const q = audit(period);
  
  if (q.isLoading) return (
    <div className="p-6">
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
      </div>
    </div>
  );
  
  const records = q.data?.entries || [];
  
  const handleExportPDF = () => {
    const headers = ['User', 'Action', 'Module', 'Timestamp', 'IP Address'];
    const data = records.map((r: any) => [
      r.user,
      r.action,
      r.module,
      new Date(r.timestamp).toLocaleString(),
      r.ipAddress || 'N/A'
    ]);
    
    exportPDF('Audit Log Report', headers, data);
  };
  
  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-[var(--color-primary)]">Audit Reports</h1>
        <div className="text-sm text-gray-600">
          Total Records: {records.length}
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <h2 className="text-lg font-semibold mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Start Date</label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
              value={period.startDate}
              onChange={e => setPeriod({...period, startDate: e.target.value})}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">End Date</label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
              value={period.endDate}
              onChange={e => setPeriod({...period, endDate: e.target.value})}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Module</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
              value={period.module}
              onChange={e => setPeriod({...period, module: e.target.value})}
            >
              <option value="">All Modules</option>
              <option value="vendors">Vendors</option>
              <option value="customers">Customers</option>
              <option value="qc">Quality Control</option>
              <option value="billing">Billing</option>
              <option value="reports">Reports</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">User</label>
            <input
              type="text"
              placeholder="Filter by user..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
              value={period.user}
              onChange={e => setPeriod({...period, user: e.target.value})}
            />
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={handleExportPDF}
            className="px-4 py-2 bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-primary)]/90 transition-colors"
          >
            Export PDF
          </button>
          <button
            onClick={() => setPeriod({startDate: '', endDate: '', module: '', user: ''})}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <h3 className="text-sm font-medium text-gray-600">Total Actions</h3>
          <p className="text-2xl font-bold text-[var(--color-primary)]">{records.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <h3 className="text-sm font-medium text-gray-600">Unique Users</h3>
          <p className="text-2xl font-bold text-[var(--color-accent)]">
            {new Set(records.map((r: any) => r.user)).size}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <h3 className="text-sm font-medium text-gray-600">Modules Accessed</h3>
          <p className="text-2xl font-bold text-[var(--color-secondary)]">
            {new Set(records.map((r: any) => r.module)).size}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <h3 className="text-sm font-medium text-gray-600">Failed Actions</h3>
          <p className="text-2xl font-bold text-red-600">
            {records.filter((r: any) => r.status === 'failed').length}
          </p>
        </div>
      </div>
      
      {/* Audit Log Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-[var(--color-accent)]/10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Module
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP Address
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {records.map((record: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {record.user}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {record.action}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {record.module}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(record.timestamp).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      record.status === 'success' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {record.status || 'success'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {record.ipAddress || 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {records.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500">No audit records found for the selected criteria.</div>
          </div>
        )}
      </div>
    </div>
  );
}
